import { useEffect, useState } from "react";
import apiCall from "../API/API";

/**
 * Which admin pages a signed-in admin can open. The main admin sees everything; a sub-admin
 * sees the modules the admin gave them (keys match SERVICES/SubAdmin.js on the API).
 * A page mapped to null is for the main admin only.
 */
export const PAGE_MODULE = {
  "": "dashboard",
  dashboard: "dashboard",
  activation_requests: "activation_requests",
  deposit_accounts: "deposit_accounts",
  all_users: "users",
  without_sponsor: "users",
  edit_profile: "users",
  register_user: "register_user",
  user_bank_details: "bank_details",
  rank: "rank",
  advisor_ranks: "rank",
  kyc_detail: "kyc",
  aadhar_kyc_detail: "kyc",
  withdraw_controls: "withdrawals",
  payout_ledger: "withdrawals",
  payout_controls: "payout_report",
  income: "payout_report",
  orders: "orders",
  news: "news",
  support: "support",
  chat: "support",
  plan_settings: null,
  id_settlement: null,
  email_req_status: null,
  sub_admins: null,
  audit_log: null,
  old_users: null,
  give_power: null,
  team_income: "team_income",
};

const STORAGE_KEY = "adminAccess";
let cached = null;
let pending = null;

const readStored = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
  } catch {
    return null;
  }
};

/** Fetches /sub-admin/me once per page load (shared by every caller). */
export const loadAdminAccess = (force = false) => {
  if (cached && !force) return Promise.resolve(cached);
  if (pending && !force) return pending;
  pending = apiCall("GET", "/sub-admin/me").then(({ data, error }) => {
    pending = null;
    if (error || !data?.data) return cached || readStored();
    cached = data.data;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(cached));
    } catch {
      /* storage unavailable */
    }
    return cached;
  });
  return pending;
};

export const clearAdminAccess = () => {
  cached = null;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* storage unavailable */
  }
};

/** Can this admin open a page (sidebar path without the /dashboard/ prefix)? */
export const canOpen = (access, page) => {
  if (!access) return false;
  if (access.role === "admin") return true;
  const module = PAGE_MODULE[page];
  return !!module && (access.modules || []).includes(module);
};

/** First page a sub-admin may open, for redirects from pages they cannot use. */
export const firstAllowedPage = (access) => {
  const order = ["", "activation_requests", "all_users", "kyc_detail", "withdraw_controls", "orders", "support", "news", "deposit_accounts", "payout_controls", "rank", "user_bank_details", "register_user", "team_income"];
  return order.find((p) => canOpen(access, p));
};

export const useAdminAccess = () => {
  const [access, setAccess] = useState(() => cached || readStored());
  useEffect(() => {
    let alive = true;
    loadAdminAccess().then((a) => alive && a && setAccess(a));
    return () => {
      alive = false;
    };
  }, []);
  return access;
};
